import { useAuth } from "@/src/contexts/auth.context";
import { useSeedUserPostReactions } from "@/src/services/api/posts/posts.hooks";
import { createContext, ReactNode, useContext, useMemo } from "react";

/**
 * Present only inside a post list. Its absence is what tells a card it is
 * standing alone (the detail modal) and should resolve its own reaction.
 */
const FeedReactionsContext = createContext<{ ready: boolean } | null>(null);

type FeedReactionsProviderProps = Readonly<{
  /** The posts currently rendered — one page, then two, as the reader scrolls. */
  postIds: readonly string[];
  children: ReactNode;
}>;

/**
 * Resolves "did I react to this?" for a whole page of posts in one request.
 *
 * Each card asks that question, so a feed of N posts used to open N queries on
 * mount — the answer arrived, but at the cost of one round trip per row, N
 * again on the next page. This fetches them together and fills the same cache
 * slots the cards read, so the cards themselves stay quiet.
 *
 * Nothing about a card changes: the react-query cache is still the single
 * source of truth, so the optimistic update a reaction tap performs, and the
 * invalidation that follows it, work exactly as before.
 */
export const FeedReactionsProvider = ({
  postIds,
  children,
}: FeedReactionsProviderProps) => {
  const { user } = useAuth();
  const { ready } = useSeedUserPostReactions(postIds, user?.itemId ?? "");
  const value = useMemo(() => ({ ready }), [ready]);

  return (
    <FeedReactionsContext.Provider value={value}>
      {children}
    </FeedReactionsContext.Provider>
  );
};

/**
 * Whether a post card may run its own reaction query.
 *
 * Inside a list: not until the batch has settled, so the two never race for the
 * same answer. Outside one: always, which is the single-post case.
 */
export const useUserReactionQueryEnabled = (): boolean => {
  const context = useContext(FeedReactionsContext);
  return context ? context.ready : true;
};
